"use client";

import React, { useEffect, useMemo, useState, type CSSProperties, type ReactNode } from "react";
import { useTranslations } from "next-intl";
import { useEditorStore } from "./editorStore";
import FontPicker from "./FontPicker";
import ImageCropModal, { buildImageCropElementPatch } from "./ImageCropModal";

type EditorConfig = {
  fonts: Array<{
    id: string;
    family: string;
    role?: "heading" | "text" | "other";
    variants: { regular: boolean; bold: boolean; italic: boolean; boldItalic: boolean };
  }>;
  frames: Array<{ id: string; type: string }>;
};

function clamp(n: number, min: number, max: number) {
  return Math.max(min, Math.min(max, n));
}

function mergeStyle(el: any, stylePatch: Record<string, any>) {
  return { ...(el?.style || {}), ...stylePatch };
}

function findElement(docData: any, elementId: string) {
  const pages = Array.isArray(docData?.pages) ? docData.pages : [];
  for (let pageIndex = 0; pageIndex < pages.length; pageIndex += 1) {
    const elements = Array.isArray(pages[pageIndex]?.elements) ? pages[pageIndex].elements : [];
    for (const element of elements) {
      if (element?.id === elementId) return { el: element, pageIndex };
    }
  }
  return null;
}

function isSamePreset(el: any, preset: any) {
  if (!preset) return false;

  const s1 = el?.style || {};
  const s2 = preset?.style || {};
  const keys = [
    "font_family",
    "font_size_pt",
    "font_weight",
    "italic",
    "underline",
    "color_hex",
    "text_align",
    "line_height",
    "padding_mm",
    "text_shadow"
  ];

  for (const key of keys) {
    if (JSON.stringify(s1[key] ?? null) !== JSON.stringify(s2[key] ?? null)) return false;
  }

  if (String(el?.text_role || "body") === "list") {
    if (String(el?.list_marker || "bullet") !== String(preset?.list_marker || "bullet")) return false;
  }

  const box = preset?.box_mm || {};
  const width = Number(el?.w_mm ?? 0);
  const height = Number(el?.h_mm ?? 0);

  if (Math.abs(width - Number(box.w ?? 0)) > 0.01) return false;
  if (Math.abs(height - Number(box.h ?? 0)) > 0.01) return false;

  return true;
}

function getRangeStyle(value: number, min: number, max: number): CSSProperties {
  const ratio = max === min ? 0 : ((value - min) / (max - min)) * 100;
  return {
    ["--range-value" as string]: `${Math.max(0, Math.min(100, ratio))}%`,
  } as CSSProperties;
}

function SectionLabel({ children }: { children: ReactNode }) {
  return <label className="editor-label block">{children}</label>;
}

function Divider() {
  return <div className="border-t border-white/8" />;
}

function SelectField({
  value,
  options,
  onChange,
}: {
  value: string;
  options: Array<{ value: string; label: string }>;
  onChange: (value: string) => void;
}) {
  return (
    <div className="editor-select-shell relative mt-3">
      <select value={value} onChange={(event) => onChange(event.target.value)} className="editor-field-select">
        {options.map((option) => (
          <option key={option.value} value={option.value}>
            {option.label}
          </option>
        ))}
      </select>
      <span className="pointer-events-none absolute right-4 top-1/2 -translate-y-1/2 text-[#c1cade]">▾</span>
    </div>
  );
}

function TextField({
  value,
  onChange,
}: {
  value: string;
  onChange: (value: string) => void;
}) {
  return (
    <div className="editor-input-shell">
      <input value={value} onChange={(event) => onChange(event.target.value)} className="editor-field-input" type="text" />
    </div>
  );
}

function RangeField({
  label,
  value,
  min,
  max,
  step = 1,
  display,
  onChange,
}: {
  label: string;
  value: number;
  min: number;
  max: number;
  step?: number;
  display: string;
  onChange: (value: number) => void;
}) {
  return (
    <div className="space-y-2.5">
      <SectionLabel>{label}</SectionLabel>
      <input
        className="editor-range"
        type="range"
        min={min}
        max={max}
        step={step}
        value={value}
        onChange={(event) => onChange(Number(event.target.value))}
        style={getRangeStyle(value, min, max)}
      />
      <p className="text-right text-sm font-medium text-[#98a4bc]">{display}</p>
    </div>
  );
}

function ColorField({
  value,
  onChange,
}: {
  value: string;
  onChange: (value: string) => void;
}) {
  return (
    <div className="mt-3 flex items-center gap-3">
      <div className="editor-color-swatch" style={{ backgroundColor: value }}>
        <input type="color" value={value} onChange={(event) => onChange(event.target.value)} />
      </div>
      <div className="flex-1">
        <TextField value={value} onChange={onChange} />
      </div>
    </div>
  );
}

function ChoiceButton({
  active,
  children,
  onClick,
  disabled,
}: {
  active?: boolean;
  children: ReactNode;
  onClick: () => void;
  disabled?: boolean;
}) {
  return (
    <button
      type="button"
      data-active={active}
      disabled={disabled}
      onClick={onClick}
      className="editor-outline-button disabled:cursor-not-allowed disabled:opacity-45"
    >
      {children}
    </button>
  );
}

function CollapsibleRow({
  label,
  open,
  onToggle,
  children,
}: {
  label: string;
  open: boolean;
  onToggle: () => void;
  children?: ReactNode;
}) {
  return (
    <div className="space-y-3">
      <button type="button" onClick={onToggle} className="editor-collapsible-row">
        <span>{label}</span>
        <span className={`transition-transform ${open ? "rotate-180 text-white" : "text-[#b4bed2]"}`}>▾</span>
      </button>
      {open ? children : null}
    </div>
  );
}

const IMAGE_MASK_SHAPE_IDS = [
  "rectangle",
  "circle",
  "triangle",
  "rhombus",
  "pentagon",
  "octagon",
  "star",
  "kite",
  "heart",
  "crescent",
  "oval",
  "semicircle"
] as const;

function ShapeIcon({ id }: { id: string }) {
  const common = { fill: "currentColor" };

  const polys: Record<string, string> = {
    triangle: "50,0 100,100 0,100",
    rhombus: "50,0 100,50 50,100 0,50",
    pentagon: "50,0 100,38 81,100 19,100 0,38",
    octagon: "30,0 70,0 100,30 100,70 70,100 30,100 0,70 0,30",
    star: "50,0 61,35 98,35 68,57 79,91 50,70 21,91 32,57 2,35 39,35",
    kite: "50,0 86,40 50,100 14,40"
  };

  const paths: Record<string, { d: string; fillRule?: "nonzero" | "evenodd" }> = {
    crescent: { d: "M77.20 22.94 A35 35 0 1 0 77.20 77.06 A28 28 0 1 1 77.20 22.94 Z" },
    heart: { d: "M50 92 C22 74 10 58 10 38 C10 20 24 10 38 10 C46 10 49 16 50 22 C51 16 54 10 62 10 C76 10 90 20 90 38 C90 58 78 74 50 92 Z" },
    oval: { d: "M50 15 A40 30 0 1 0 50 85 A40 30 0 1 0 50 15 Z" },
    semicircle: { d: "M10 60 A40 40 0 0 1 90 60 L90 100 L10 100 Z" }
  };

  return (
    <svg viewBox="0 0 100 100" className="h-8 w-8" aria-hidden="true">
      {id === "rectangle" ? <rect x="10" y="10" width="80" height="80" rx="10" {...common} /> : null}
      {id === "circle" ? <circle cx="50" cy="50" r="40" {...common} /> : null}
      {polys[id] ? <polygon points={polys[id]} {...common} /> : null}
      {paths[id] ? <path d={paths[id].d} fillRule={paths[id].fillRule || "nonzero"} {...common} /> : null}
    </svg>
  );
}

function LayerControls({
  t,
  disabled,
  onFront,
  onBack,
  onForward,
  onBackward,
}: {
  t: (key: string, values?: Record<string, string | number | Date>) => string;
  disabled?: boolean;
  onFront: () => void;
  onBack: () => void;
  onForward: () => void;
  onBackward: () => void;
}) {
  return (
    <div className="space-y-3">
      <SectionLabel>{t("common.layers")}</SectionLabel>
      <div className="grid grid-cols-2 gap-2">
        <ChoiceButton disabled={disabled} onClick={onBackward}>
          {t("common.backward")}
        </ChoiceButton>
        <ChoiceButton disabled={disabled} onClick={onForward}>
          {t("common.forward")}
        </ChoiceButton>
        <ChoiceButton disabled={disabled} onClick={onBack}>
          {t("common.back")}
        </ChoiceButton>
        <ChoiceButton disabled={disabled} onClick={onFront}>
          {t("common.front")}
        </ChoiceButton>
      </div>
    </div>
  );
}

export default function PropertiesPanel(props: {
  config: EditorConfig;
  formats?: any[];
  textPresets?: Record<string, any>;
  onSaveTextPreset?: (
    role: "heading" | "body" | "list",
    preset: { style: any; box_mm: { w: number; h: number }; list_marker?: "bullet" | "number" }
  ) => Promise<void> | void;
}) {
  const { config, textPresets, onSaveTextPreset } = props;
  const t = useTranslations("editor.properties");
  const store = useEditorStore();
  const doc = store.selected.doc;
  const elementId = store.selected.elementId;
  const docData = doc === "content" ? store.contentData : store.coverData;

  const found = useMemo(() => {
    if (!docData || !elementId) return null;
    return findElement(docData, elementId);
  }, [docData, elementId]);

  const el = found?.el || null;
  const elementPageIndex = found?.pageIndex ?? 0;

  const fontInfo = useMemo(() => {
    if (!el || el.type !== "text") return null;
    const family = String(el.style?.font_family || "");
    return config.fonts.find((font) => font.family === family) || null;
  }, [config.fonts, el]);

  const [textShadowOpen, setTextShadowOpen] = useState(false);
  const [maskOpen, setMaskOpen] = useState(false);
  const [filtersOpen, setFiltersOpen] = useState(false);
  const [cropModalOpen, setCropModalOpen] = useState(false);

  useEffect(() => {
    setTextShadowOpen(false);
    setMaskOpen(false);
    setFiltersOpen(false);
    setCropModalOpen(false);
  }, [elementId]);

  if (!elementId || !el) {
    return (
      <aside className="space-y-3 rounded-[14px] border border-white/10 bg-[#2b3550] p-4">
        <h2 className="text-sm font-semibold uppercase tracking-wide text-white">{t("common.title")}</h2>
        <div className="text-sm text-[#98a4bc]">{t("common.selectElement")}</div>
      </aside>
    );
  }

  const update = (patch: Record<string, any>) => store.updateElement(doc, elementId, patch);
  const bringToFront = () => store.bringToFront(doc, elementId);
  const sendToBack = () => store.sendToBack(doc, elementId);
  const moveForward = () => store.moveForward(doc, elementId);
  const moveBackward = () => store.moveBackward(doc, elementId);
  const deleteElement = () => store.deleteElement(doc, elementId);

  if (el.type === "text") {
    const family = String(el.style?.font_family || config.fonts[0]?.family || "Raleway");
    const fontSizePt = Number(el.style?.font_size_pt ?? 18);
    const weight = Number(el.style?.font_weight ?? 400);
    const italic = Boolean(el.style?.italic);
    const underline = Boolean(el.style?.underline);
    const color = String(el.style?.color_hex || "#000000");
    const align = String(el.style?.text_align || "left");
    const lineHeight = Number(el.style?.line_height ?? 1.5);
    const shadow = el.style?.text_shadow || {};
    const shadowEnabled = Boolean(shadow.enabled);
    const shadowBlur = Number(shadow.blur_px ?? 6);
    const shadowX = Number(shadow.offset_x_px ?? 0);
    const shadowY = Number(shadow.offset_y_px ?? 2);
    const shadowColor = String(shadow.color_hex ?? "#000000");
    const shadowOpacity = Number(shadow.opacity ?? 0.35);
    const italicAllowed = fontInfo?.variants?.italic ?? true;
    const boldAllowed = fontInfo?.variants?.bold ?? true;
    const role = String(el.text_role || "body") as "heading" | "body" | "list";
    const isListRole = role === "list";
    const fontSizeMax = role === "heading" ? 172 : 72;
    const roleLabel =
      role === "heading" ? t("text.roleHeading") : role === "list" ? t("text.roleList") : t("text.roleBody");
    const listMarker = String(el.list_marker || "bullet");
    const presetForRole = textPresets?.[role];
    const presetChecked = isSamePreset(el, presetForRole);
    const updateStyle = (stylePatch: Record<string, any>) => update({ style: mergeStyle(el, stylePatch) });
    const updateShadow = (shadowPatch: Record<string, any>) => {
      const nextShadow = { ...(el.style?.text_shadow || {}), ...shadowPatch };
      updateStyle({ text_shadow: nextShadow });
    };

    return (
      <aside className="space-y-5">
        {/* <div className="flex items-center justify-between">
          <div className="text-sm font-medium text-[#98a4bc]">{roleLabel}</div>
          <button type="button" className="editor-outline-button h-9 px-3 text-xs" onClick={() => store.requestInlineEdit(doc, elementId)}>
            {t("text.editInline")}
          </button>
        </div> */}

        {role === "list" ? (
          <div className="space-y-3">
            <SectionLabel>{t("text.listStyle")}</SectionLabel>
            <div className="grid grid-cols-2 gap-2">
              <ChoiceButton active={listMarker === "bullet"} onClick={() => update({ list_marker: "bullet" })}>
                {t("text.listBullets")}
              </ChoiceButton>
              <ChoiceButton active={listMarker === "number"} onClick={() => update({ list_marker: "number" })}>
                {t("text.listNumbers")}
              </ChoiceButton>
            </div>
          </div>
        ) : null}

        <div>
          <SectionLabel>{t("text.font")}</SectionLabel>
          <FontPicker
            fonts={config.fonts}
            value={family}
            onSelect={(value) => {
              const nextFont = config.fonts.find((font) => font.family === value);
              const canItalic = nextFont?.variants?.italic ?? true;
              const canBold = nextFont?.variants?.bold ?? true;
              updateStyle({
                font_family: value,
                italic: canItalic ? italic : false,
                font_weight: canBold ? weight : 400
              });
            }}
          />

          {italic && !italicAllowed ? <div className="mt-2 text-xs text-amber-300">{t("text.noItalic")}</div> : null}
        </div>

        <RangeField
          label={t("text.sizePt")}
          value={fontSizePt}
          min={8}
          max={fontSizeMax}
          display={`${fontSizePt}pt`}
          onChange={(value) => updateStyle({ font_size_pt: value })}
        />

        <RangeField
          label={t("text.lineHeight")}
          value={lineHeight}
          min={1}
          max={3}
          step={0.1}
          display={lineHeight.toFixed(1)}
          onChange={(value) => updateStyle({ line_height: clamp(value, 1, 3) })}
        />

        <div>
          <SectionLabel>{t("text.color")}</SectionLabel>
          <ColorField value={color} onChange={(value) => updateStyle({ color_hex: value })} />
        </div>

        {!isListRole ? (
          <>
            <div className="space-y-3">
              <SectionLabel>{t("text.weight")}</SectionLabel>
              <div className="grid grid-cols-3 gap-2">
                <ChoiceButton active={weight >= 600} disabled={!boldAllowed} onClick={() => updateStyle({ font_weight: weight >= 600 ? 400 : 700 })}>
                  Bold
                </ChoiceButton>
                <ChoiceButton active={italic} disabled={!italicAllowed} onClick={() => updateStyle({ italic: !italic })}>
                  {t("text.italic")}
                </ChoiceButton>
                <ChoiceButton active={underline} onClick={() => updateStyle({ underline: !underline })}>
                  {t("text.underline")}
                </ChoiceButton>
              </div>
            </div>

            <div className="space-y-3">
              <SectionLabel>{t("text.align")}</SectionLabel>
              <div className="grid grid-cols-4 gap-2">
                <ChoiceButton active={align === "left"} onClick={() => updateStyle({ text_align: "left" })}>
                  {t("text.alignLeft")}
                </ChoiceButton>
                <ChoiceButton active={align === "center"} onClick={() => updateStyle({ text_align: "center" })}>
                  {t("text.alignCenter")}
                </ChoiceButton>
                <ChoiceButton active={align === "right"} onClick={() => updateStyle({ text_align: "right" })}>
                  {t("text.alignRight")}
                </ChoiceButton>
                <ChoiceButton active={align === "justify"} onClick={() => updateStyle({ text_align: "justify" })}>
                  {t("text.alignJustify")}
                </ChoiceButton>
              </div>
            </div>

            <Divider />
            <CollapsibleRow label={t("text.shadow")} open={textShadowOpen} onToggle={() => setTextShadowOpen((current) => !current)}>
              <div className="space-y-4 rounded-[12px] bg-[#3f4d67]/55 p-3">
                <label className="flex items-center gap-2 text-sm font-medium text-white">
                  <input
                    type="checkbox"
                    checked={shadowEnabled}
                    onChange={(event) => {
                      const enabled = event.target.checked;
                      updateShadow({
                        enabled,
                        blur_px: shadowBlur,
                        offset_x_px: shadowX,
                        offset_y_px: shadowY,
                        color_hex: shadowColor,
                        opacity: shadowOpacity
                      });
                    }}
                    className="editor-checkbox"
                  />
                  <span>{t("text.shadowEnable")}</span>
                </label>

                <div className={`space-y-4 ${shadowEnabled ? "" : "pointer-events-none opacity-45"}`}>
                  <div>
                    <SectionLabel>{t("text.shadowColor")}</SectionLabel>
                    <ColorField value={shadowColor} onChange={(value) => updateShadow({ color_hex: value })} />
                  </div>
                  <RangeField
                    label={t("text.shadowOpacity")}
                    value={Math.round(shadowOpacity * 100)}
                    min={0}
                    max={100}
                    display={`${Math.round(shadowOpacity * 100)}%`}
                    onChange={(value) => updateShadow({ opacity: clamp(value / 100, 0, 1) })}
                  />
                  <RangeField
                    label={t("text.shadowBlur")}
                    value={shadowBlur}
                    min={0}
                    max={20}
                    display={`${shadowBlur}px`}
                    onChange={(value) => updateShadow({ blur_px: value })}
                  />
                  <RangeField
                    label={t("text.shadowOffsetX")}
                    value={shadowX}
                    min={-40}
                    max={40}
                    display={`${shadowX}px`}
                    onChange={(value) => updateShadow({ offset_x_px: value })}
                  />
                  <RangeField
                    label={t("text.shadowOffsetY")}
                    value={shadowY}
                    min={-40}
                    max={40}
                    display={`${shadowY}px`}
                    onChange={(value) => updateShadow({ offset_y_px: value })}
                  />
                </div>
              </div>
            </CollapsibleRow>
          </>
        ) : null}

        <Divider />
        <LayerControls t={t} onFront={bringToFront} onBack={sendToBack} onForward={moveForward} onBackward={moveBackward} />

        {onSaveTextPreset ? (
          <>
            <Divider />
            <label className="flex items-start gap-2 text-sm font-medium text-white/90">
              <input
                type="checkbox"
                checked={presetChecked}
                onChange={async (event) => {
                  if (!event.target.checked) return;
                  await onSaveTextPreset(role, {
                    style: { ...(el.style || {}) },
                    box_mm: { w: Number(el.w_mm ?? 120), h: Number(el.h_mm ?? 30) },
                    ...(role === "list" ? { list_marker: listMarker } : {})
                  } as any);
                }}
                className="editor-checkbox mt-1"
              />
              <span>{t("text.savePresetForRole", { role: roleLabel })}</span>
            </label>
          </>
        ) : null}

        <Divider />
        <button type="button" className="editor-danger-button" onClick={deleteElement}>
          {t("common.deleteElement")}
        </button>
      </aside>
    );
  }

  if (el.type === "shape") {
    const shapeId = String(el.shape_id || "rectangle");
    const fill = String(el.style?.fill_hex || "#111827");
    const stroke = String(el.style?.stroke_hex || "#111827");
    const strokeWidth = Number(el.style?.stroke_width_mm ?? 0);
    const opacity = Math.round((el.opacity == null ? 1 : Number(el.opacity)) * 100);
    const shapeLabelMap: Record<string, string> = {
      rectangle: t("shape.rectangle"),
      circle: t("shape.circle"),
      triangle: t("shape.triangle"),
      star: t("shape.star"),
      pentagon: t("shape.pentagon"),
      octagon: t("shape.octagon"),
      rhombus: t("shape.rhombus"),
      heart: t("shape.heart"),
      crescent: t("shape.crescent"),
      kite: t("shape.kite"),
      oval: t("shape.oval"),
      semicircle: t("shape.semicircle")
    };

    return (
      <aside className="space-y-5">
        {/* <div className="text-sm font-medium text-[#98a4bc]">{shapeLabelMap[shapeId] || shapeId}</div> */}

        <div>
          <SectionLabel>{t("shape.fill")}</SectionLabel>
          <ColorField value={fill} onChange={(value) => update({ style: mergeStyle(el, { fill_hex: value }) })} />
        </div>

        <div>
          <SectionLabel>{t("shape.stroke")}</SectionLabel>
          <ColorField value={stroke} onChange={(value) => update({ style: mergeStyle(el, { stroke_hex: value }) })} />
        </div>

        <RangeField
          label={t("shape.strokeWidth")}
          value={strokeWidth}
          min={0}
          max={20}
          step={0.1}
          display={`${strokeWidth}px`}
          onChange={(value) => update({ style: mergeStyle(el, { stroke_width_mm: clamp(value, 0, 20) }) })}
        />

        <RangeField
          label={t("shape.opacity")}
          value={opacity}
          min={0}
          max={100}
          display={`${opacity}%`}
          onChange={(value) => update({ opacity: clamp(value / 100, 0, 1) })}
        />

        <Divider />
        <LayerControls t={t} onFront={bringToFront} onBack={sendToBack} onForward={moveForward} onBackward={moveBackward} />

        <Divider />
        <button type="button" className="editor-danger-button" onClick={deleteElement}>
          {t("common.deleteElement")}
        </button>
      </aside>
    );
  }

  if (el.type === "rect" || el.type === "background") {
    const fill = String(el.style?.fill_hex || "#ff0000");
    const label = el.type === "background" ? t("shape.background") : t("shape.rect");
    const isBackground = el.type === "background" || Boolean(el.is_background);

    return (
      <aside className="space-y-5">
        {/* <div className="text-sm font-medium text-[#98a4bc]">{label}</div> */}

        <div>
          <SectionLabel>{t("shape.fill")}</SectionLabel>
          <ColorField value={fill} onChange={(value) => update({ style: mergeStyle(el, { fill_hex: value }) })} />
        </div>

        {!isBackground ? (
          <>
            <Divider />
            <LayerControls t={t} onFront={bringToFront} onBack={sendToBack} onForward={moveForward} onBackward={moveBackward} />
          </>
        ) : (
          <div className="rounded-[12px] border border-white/10 bg-[#3f4d67]/45 p-3 text-sm text-[#98a4bc]">
            {t("common.layersDisabled")}
          </div>
        )}

        <Divider />
        <button type="button" className="editor-danger-button" onClick={deleteElement}>
          {t("common.deleteElement")}
        </button>
      </aside>
    );
  }

  if (el.type === "image") {
    const frameId = String(el.frame_id || "");
    const fit = String(el.fit || "contain");
    const rotate = Number(el.rotate_deg ?? 0);
    const opacity = Math.round((el.opacity == null ? 1 : Number(el.opacity)) * 100);
    const scale = Math.round(Number(el.scale ?? 1) * 100);
    const preset = String(el.filter?.preset || "none");
    const brightness = Math.round(Number(el.filter?.brightness ?? 1) * 100);
    const contrast = Math.round(Number(el.filter?.contrast ?? 1) * 100);
    const saturation = Math.round(Number(el.filter?.saturation ?? 1) * 100);
    const imageUrl = String(el.image_url || "");
    const selectedMaskShape = el.clip?.type === "shape" ? String(el.clip?.shape_id || "") : "";
    const isSpreadUnit =
      doc === "content" &&
      String(store.contentData?.meta?.layout_mode || "pages") === "book_spreads" &&
      String(store.contentData?.pages?.[elementPageIndex]?.kind || "single") === "spread";

    return (
      <aside className="space-y-5">
        <div>
          <SectionLabel>{t("image.frame")}</SectionLabel>
          <SelectField
            value={frameId}
            options={[
              { value: "", label: t("image.none") },
              ...config.frames.map((frame) => ({ value: frame.id, label: `${frame.id} (${frame.type})` }))
            ]}
            onChange={(value) => update({ frame_id: value || null })}
          />
        </div>

        <div>
          <SectionLabel>{t("image.fit")}</SectionLabel>
          <SelectField
            value={fit}
            options={[
              { value: "cover", label: t("image.fitCover") },
              { value: "contain", label: t("image.fitContain") }
            ]}
            onChange={(value) => update({ fit: value })}
          />
        </div>

        {isSpreadUnit ? <div className="text-sm text-[#98a4bc]">{t("image.spreadHint")}</div> : null}

        <RangeField
          label={t("image.rotateDeg")}
          value={rotate}
          min={-180}
          max={180}
          display={`${rotate}°`}
          onChange={(value) => update({ rotate_deg: value })}
        />

        <RangeField
          label={t("image.opacity")}
          value={opacity}
          min={0}
          max={100}
          display={`${opacity}%`}
          onChange={(value) => update({ opacity: clamp(value / 100, 0, 1) })}
        />

        <RangeField
          label={t("image.scaleZoom")}
          value={scale}
          min={10}
          max={500}
          display={`${scale}%`}
          onChange={(value) => update({ scale: clamp(value / 100, 0.1, 10) })}
        />

        <button
          type="button"
          className="editor-accent-button h-11 w-full"
          style={{
            background: "linear-gradient(135deg, var(--editor-gradient-start), var(--editor-gradient-end))",
            borderColor: "rgba(255, 255, 255, 0.75)",
            boxShadow:
              "rgba(255, 255, 255, 0.18) 0px 0px 0px 1px inset, rgba(124, 44, 255, 0.18) 0px 12px 24px",
          }}
          onClick={() => setCropModalOpen(true)}
          disabled={!imageUrl}
        >
          {t("image.cropImage")}
        </button>

        <button
          type="button"
          className="editor-outline-button h-11 w-full bg-white/6 text-[#98a4bc]"
          onClick={() => update({ fit: "contain", scale: 1, pan_mm: { x: 0, y: 0 }, rotate_deg: 0 })}
        >
          {t("image.resetCrop")}
        </button>

        <Divider />
        <CollapsibleRow label={t("image.mask")} open={maskOpen} onToggle={() => setMaskOpen((current) => !current)}>
          <div className="grid grid-cols-3 gap-2">
            <button
              type="button"
              data-active={!selectedMaskShape}
              className="editor-icon-tile"
              onClick={() => update({ clip: { type: "none" } })}
            >
              <span className="text-sm font-semibold">{t("image.maskNone")}</span>
            </button>
            {IMAGE_MASK_SHAPE_IDS.map((shapeId) => (
              <button
                key={shapeId}
                type="button"
                data-active={selectedMaskShape === shapeId}
                title={t(`shape.${shapeId}` as any)}
                aria-label={t(`shape.${shapeId}` as any)}
                className="editor-icon-tile"
                onClick={() => update({ clip: { type: "shape", shape_id: shapeId } })}
              >
                <ShapeIcon id={shapeId} />
              </button>
            ))}
          </div>
        </CollapsibleRow>

        {cropModalOpen && imageUrl ? (
          <ImageCropModal
            imageUrl={imageUrl}
            frameWmm={Math.max(1, Number(el.w_mm || 1))}
            frameHmm={Math.max(1, Number(el.h_mm || 1))}
            initialScale={Number(el.scale ?? 1)}
            initialPanMm={{
              x: Number(el.pan_mm?.x ?? 0),
              y: Number(el.pan_mm?.y ?? 0),
            }}
            labels={{
              title: t("image.cropTitle"),
              apply: t("image.cropApply"),
              cancel: t("image.cropCancel"),
              hint: t("image.cropHint"),
            }}
            onClose={() => setCropModalOpen(false)}
            onApply={(patch) => {
              update(buildImageCropElementPatch(el, patch));
              setCropModalOpen(false);
            }}
          />
        ) : null}

        <Divider />
        <CollapsibleRow label={t("image.filters")} open={filtersOpen} onToggle={() => setFiltersOpen((current) => !current)}>
          <div className="space-y-4 rounded-[12px] bg-[#3f4d67]/55 p-3">
            <div>
              <SectionLabel>{t("image.preset")}</SectionLabel>
              <SelectField
                value={preset}
                options={[
                  { value: "none", label: t("image.presetNone") },
                  { value: "sepia", label: t("image.presetSepia") },
                  { value: "grayscale", label: t("image.presetGrayscale") },
                  { value: "invert", label: t("image.presetInvert") }
                ]}
                onChange={(value) => update({ filter: { ...(el.filter || {}), preset: value } })}
              />
            </div>

            <RangeField
              label={t("image.brightness")}
              value={brightness}
              min={0}
              max={300}
              display={`${brightness}%`}
              onChange={(value) => update({ filter: { ...(el.filter || {}), brightness: value / 100 } })}
            />
            <RangeField
              label={t("image.contrast")}
              value={contrast}
              min={0}
              max={300}
              display={`${contrast}%`}
              onChange={(value) => update({ filter: { ...(el.filter || {}), contrast: value / 100 } })}
            />
            <RangeField
              label={t("image.saturation")}
              value={saturation}
              min={0}
              max={300}
              display={`${saturation}%`}
              onChange={(value) => update({ filter: { ...(el.filter || {}), saturation: value / 100 } })}
            />
          </div>
        </CollapsibleRow>

        <div className="text-sm text-[#98a4bc]">{t("image.filterHint")}</div>

        <Divider />
        <LayerControls t={t} onFront={bringToFront} onBack={sendToBack} onForward={moveForward} onBackward={moveBackward} />

        <Divider />
        <button type="button" className="editor-danger-button" onClick={deleteElement}>
          {t("common.deleteElement")}
        </button>
      </aside>
    );
  }

  return (
    <aside className="space-y-3 rounded-[14px] border border-white/10 bg-[#2b3550] p-4">
      <h2 className="text-sm font-semibold uppercase tracking-wide text-white">{t("common.title")}</h2>
      <div className="text-sm text-[#98a4bc]">{t("common.unsupported")}</div>
    </aside>
  );
}
